import { JsonType, PostHogConfig } from 'posthog-js';
import { ZodSchema } from 'zod';

import { FeatureFlagsEnum } from '@eluve/graphql-types';
import { MatchingRecord } from '@eluve/utils';

import { booleanFeatureFlag } from './boolean-feature-flag';
import { chatAgentPrompts } from './chat-agent-prompts';
import { chunkedBatchTranscriptionDuration } from './chunked-batch-transcription';
import { siloamPatientChartBackfill } from './siloam-patient-chart-backfill';
import { transcriptionConfigurations } from './transcription-configurations';
import { transcriptionEvalConfigurations } from './transcription-eval-configurations';
import { SchemaWithDefaults } from './utils';

export type FeatureFlagDefinitions = Record<
  string,
  SchemaWithDefaults<ZodSchema<JsonType>>
>;

export const featureFlagsWithPayloads = {
  CHUNKED_BATCH_TRANSCRIPTION_DURATION_MINUTES:
    chunkedBatchTranscriptionDuration,
  TRANSCRIPTION_CONFIGURATIONS: transcriptionConfigurations,
  TRANSCRIPTION_EVAL_CONFIGURATIONS: transcriptionEvalConfigurations,
  MULTILINGUAL_SUPPORT: booleanFeatureFlag,
  ALLOW_RERUN_TRANSCRIPTION: booleanFeatureFlag,
  CHAT_AGENT_PROMPTS: chatAgentPrompts,
  BLVD_SERVICE_CANCELLATION: booleanFeatureFlag,
  ENABLE_MICROSOFT_LOGIN: booleanFeatureFlag,
  INTERCOM_INTEGRATION: booleanFeatureFlag,
  USE_FDA_DRUGS: booleanFeatureFlag,
  INSIGHTS_CHAT: booleanFeatureFlag,
  ELUVE_GPT: booleanFeatureFlag,
  AUDIO_STACK_V2: booleanFeatureFlag,
  AUDIO_STACK_V2_FORCE_BLOB: booleanFeatureFlag,
  DEPRECATE_V1_AUDIO: booleanFeatureFlag,
  USE_MODEL_FAILOVER: booleanFeatureFlag,
  USE_STREAMING_SUMMARIZATION: booleanFeatureFlag,
  EXTENSION_CHAT: booleanFeatureFlag,
  EXTENSION_SILOAM_API_SYNC_ENABLED: booleanFeatureFlag,
  BACKEND_SILENCE_DETECTION: booleanFeatureFlag,
  EXTENSION_PREVISIT_INSIGHTS: booleanFeatureFlag,
  WEB_PREVISIT_INSIGHTS: booleanFeatureFlag,
  EXTENSION_DDX_INSIGHTS: booleanFeatureFlag,
  SILOAM_PATIENT_CHART_BACKFILL: siloamPatientChartBackfill,
  WEB_DEMO_REFERRAL_FLOW: booleanFeatureFlag,
  SHOW_EARLY_ACCESS_PANEL: booleanFeatureFlag,
  MASTRA_AGENT_CHAT: { ...booleanFeatureFlag, requiresServerEvaluation: true },
  MASTRA_SUPERVISOR_CHAT: {
    ...booleanFeatureFlag,
    requiresServerEvaluation: true,
  },
  EXTENSION_UI_TWEAKS: booleanFeatureFlag,
  MAINTENANCE_MODE: booleanFeatureFlag,
  CHAT_SPEC_TEMPLATES: booleanFeatureFlag,
  BILLING_CODE_RECOMMENDER_ENABLED: booleanFeatureFlag,
  EXTENSION_AUDIO_FILE_DROP: booleanFeatureFlag,
  CHAT_KNOWLEDGE_BASES: {
    ...booleanFeatureFlag,
    requiresServerEvaluation: true,
  },
  USE_POSTHOG_PROXY: booleanFeatureFlag,
  RECORD_SESSION: booleanFeatureFlag,
  CARE_PLANS: booleanFeatureFlag,
  // User-level v1 rollout of the Clinical Tasks UI (panel + related surfaces).
  // Same key as the tenant-level flag, which gates extraction itself.
  CLINICAL_TASKS: booleanFeatureFlag,
  // Per-user scoping of the new patient profile. Same key as the tenant-level
  // flag, which is a prerequisite: both must be on.
  NEW_PATIENT_PROFILE: booleanFeatureFlag,
} as const satisfies FeatureFlagDefinitions;

export type FeatureFlagKeys = keyof typeof featureFlagsWithPayloads;

export type TenantOrUserFeatureFlagKeys = Extract<
  FeatureFlagsEnum,
  FeatureFlagKeys
>;

export const FeatureFlagKeysLookup = {
  TRANSCRIPTION_CONFIGURATIONS: 'TRANSCRIPTION_CONFIGURATIONS',
  TRANSCRIPTION_EVAL_CONFIGURATIONS: 'TRANSCRIPTION_EVAL_CONFIGURATIONS',
  MULTILINGUAL_SUPPORT: 'MULTILINGUAL_SUPPORT',
  ALLOW_RERUN_TRANSCRIPTION: 'ALLOW_RERUN_TRANSCRIPTION',
  CHAT_AGENT_PROMPTS: 'CHAT_AGENT_PROMPTS',
  CHUNKED_BATCH_TRANSCRIPTION_DURATION_MINUTES:
    'CHUNKED_BATCH_TRANSCRIPTION_DURATION_MINUTES',
  BLVD_SERVICE_CANCELLATION: 'BLVD_SERVICE_CANCELLATION',
  ENABLE_MICROSOFT_LOGIN: 'ENABLE_MICROSOFT_LOGIN',
  INTERCOM_INTEGRATION: 'INTERCOM_INTEGRATION',
  USE_FDA_DRUGS: 'USE_FDA_DRUGS',
  INSIGHTS_CHAT: 'INSIGHTS_CHAT',
  ELUVE_GPT: 'ELUVE_GPT',
  AUDIO_STACK_V2: 'AUDIO_STACK_V2',
  AUDIO_STACK_V2_FORCE_BLOB: 'AUDIO_STACK_V2_FORCE_BLOB',
  DEPRECATE_V1_AUDIO: 'DEPRECATE_V1_AUDIO',
  USE_MODEL_FAILOVER: 'USE_MODEL_FAILOVER',
  USE_STREAMING_SUMMARIZATION: 'USE_STREAMING_SUMMARIZATION',
  EXTENSION_CHAT: 'EXTENSION_CHAT',
  EXTENSION_SILOAM_API_SYNC_ENABLED: 'EXTENSION_SILOAM_API_SYNC_ENABLED',
  BACKEND_SILENCE_DETECTION: 'BACKEND_SILENCE_DETECTION',
  EXTENSION_PREVISIT_INSIGHTS: 'EXTENSION_PREVISIT_INSIGHTS',
  WEB_PREVISIT_INSIGHTS: 'WEB_PREVISIT_INSIGHTS',
  SILOAM_PATIENT_CHART_BACKFILL: 'SILOAM_PATIENT_CHART_BACKFILL',
  EXTENSION_DDX_INSIGHTS: 'EXTENSION_DDX_INSIGHTS',
  USE_POSTHOG_PROXY: 'USE_POSTHOG_PROXY',
  WEB_DEMO_REFERRAL_FLOW: 'WEB_DEMO_REFERRAL_FLOW',
  SHOW_EARLY_ACCESS_PANEL: 'SHOW_EARLY_ACCESS_PANEL',
  MASTRA_AGENT_CHAT: 'MASTRA_AGENT_CHAT',
  MASTRA_SUPERVISOR_CHAT: 'MASTRA_SUPERVISOR_CHAT',
  EXTENSION_UI_TWEAKS: 'EXTENSION_UI_TWEAKS',
  MAINTENANCE_MODE: 'MAINTENANCE_MODE',
  CHAT_SPEC_TEMPLATES: 'CHAT_SPEC_TEMPLATES',
  BILLING_CODE_RECOMMENDER_ENABLED: 'BILLING_CODE_RECOMMENDER_ENABLED',
  EXTENSION_AUDIO_FILE_DROP: 'EXTENSION_AUDIO_FILE_DROP',
  CHAT_KNOWLEDGE_BASES: 'CHAT_KNOWLEDGE_BASES',
  RECORD_SESSION: 'RECORD_SESSION',
  CARE_PLANS: 'CARE_PLANS',
  CLINICAL_TASKS: 'CLINICAL_TASKS',
  NEW_PATIENT_PROFILE: 'NEW_PATIENT_PROFILE',
} satisfies MatchingRecord<FeatureFlagKeys>;

type PostHogBootstrap = PostHogConfig['bootstrap'];

export const makeBootstrappedFlags = (
  input: Record<string, SchemaWithDefaults<ZodSchema<JsonType>>>,
): Pick<PostHogBootstrap, 'featureFlagPayloads' | 'featureFlags'> => {
  const featureFlags: PostHogBootstrap['featureFlags'] = {};
  const featureFlagPayloads: PostHogBootstrap['featureFlagPayloads'] = {};

  for (const [key, val] of Object.entries(input)) {
    featureFlags[key] = val.defaultEnabled;
    featureFlagPayloads[key] = val.defaultValue;
  }

  return {
    featureFlagPayloads,
    featureFlags,
  };
};

export const bootstrappedFlags = makeBootstrappedFlags(
  featureFlagsWithPayloads,
);
